import { ethers } from 'ethers';
import { readContractData } from './contractInstance';

export const MAX_FEE_BPS = 1000;

export const FEE_PAYMENT_TYPES = [
    { value: 0, key: 'directPaymentBps', label: 'Direct payment' },
    { value: 1, key: 'lockedEscrowBps', label: 'Locked escrow' },
    { value: 2, key: 'lockedClaimBps', label: 'Locked claim' },
    { value: 3, key: 'disputeResolveBps', label: 'Dispute resolve' },
];

const readOptional = async (readContract, address, abi, names, args = []) => {
    const candidates = Array.isArray(names) ? names : [names];
    for (const functionName of candidates) {
        try {
            return await readContract(address, abi, functionName, args);
        } catch (_error) {
            // try next candidate
        }
    }
    return null;
};

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const normalizeAddress = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
        return ethers.getAddress(raw);
    } catch (_error) {
        return raw.toLowerCase();
    }
};

export const bpsToPercentLabel = (bps) => {
    const value = toNumber(bps);
    if (value === 0) return '0%';
    return `${(value / 100).toFixed(value % 100 === 0 ? 0 : 2)}%`;
};

export const percentInputToBps = (percentText) => {
    const parsed = Number(String(percentText || '').trim());
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    const bps = Math.round(parsed * 100);
    if (bps > MAX_FEE_BPS) return null;
    return bps;
};

export const hoursInputToSeconds = (hoursText) => {
    const parsed = Number(String(hoursText || '').trim());
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return Math.round(parsed * 3600);
};

export const secondsToHoursLabel = (seconds) => {
    const hours = toNumber(seconds) / 3600;
    if (!hours) return '0 hours';
    if (hours >= 24) {
        const days = hours / 24;
        return `${days.toFixed(days >= 10 ? 0 : 1)} days`;
    }
    return `${hours.toFixed(hours >= 10 ? 0 : 1)} hours`;
};

const mapWhitelistedTokens = (addresses = [], tokenList = []) => {
    const byAddress = new Map(
        tokenList.map((token) => [
            String(token.address || '').toLowerCase(),
            token,
        ]),
    );

    return addresses.map((address) => {
        const normalized = normalizeAddress(address);
        const meta = byAddress.get(String(address || '').toLowerCase());
        return {
            address: normalized,
            symbol: meta?.symbol || null,
            name: meta?.name || null,
            logoURI: meta?.logoURI || null,
        };
    });
};

export async function fetchGatewayAdminSettings({
    gatewayAddress,
    gatewayAbi,
    tokenList = [],
    readContract = readContractData,
}) {
    if (!gatewayAddress) {
        return null;
    }

    const [
        owner,
        disputeAdmin,
        paused,
        paymentFees,
        disputeResolutionPeriod,
        minDisputeResolutionPeriod,
        maxPaymentAmount,
        adminTimelockDelay,
        pendingFeeRecipient,
        feeRecipientEffectiveAt,
        pendingDisputeAdmin,
        disputeAdminEffectiveAt,
        whitelistedTokenList,
    ] = await Promise.all([
        readOptional(readContract, gatewayAddress, gatewayAbi, 'owner'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'disputeAdmin'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'paused'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'getPaymentFees'),
        readOptional(readContract, gatewayAddress, gatewayAbi, [
            'disputeResolutionPeriod',
            'getDisputeResolutionPeriod',
        ]),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'minDisputeResolutionPeriod'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'maxPaymentAmount'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'adminTimelockDelay'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'pendingFeeRecipient'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'feeRecipientEffectiveAt'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'pendingDisputeAdmin'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'disputeAdminEffectiveAt'),
        readOptional(readContract, gatewayAddress, gatewayAbi, 'whitelistedTokenList'),
    ]);

    const fees = paymentFees
        ? {
              recipient: normalizeAddress(
                  paymentFees.recipient ?? paymentFees[0] ?? '',
              ),
              directPaymentBps: toNumber(
                  paymentFees.directPaymentBps ?? paymentFees[1],
              ),
              lockedEscrowBps: toNumber(
                  paymentFees.lockedEscrowBps ?? paymentFees[2],
              ),
              lockedClaimBps: toNumber(
                  paymentFees.lockedClaimBps ?? paymentFees[3],
              ),
              disputeResolveBps: toNumber(
                  paymentFees.disputeResolveBps ?? paymentFees[4],
              ),
          }
        : {
              recipient: '',
              directPaymentBps: 0,
              lockedEscrowBps: 0,
              lockedClaimBps: 0,
              disputeResolveBps: 0,
          };

    const tokenAddresses = Array.isArray(whitelistedTokenList)
        ? whitelistedTokenList
        : [];

    return {
        address: gatewayAddress,
        owner: normalizeAddress(owner),
        disputeAdmin: normalizeAddress(disputeAdmin),
        paused: Boolean(paused),
        fees,
        disputeResolutionPeriodSeconds: toNumber(disputeResolutionPeriod),
        minDisputeResolutionPeriodSeconds: toNumber(minDisputeResolutionPeriod),
        maxPaymentAmount: maxPaymentAmount?.toString?.() ?? String(maxPaymentAmount ?? '0'),
        adminTimelockDelaySeconds: toNumber(adminTimelockDelay),
        pendingFeeRecipient: normalizeAddress(pendingFeeRecipient),
        feeRecipientEffectiveAt: toNumber(feeRecipientEffectiveAt),
        pendingDisputeAdmin: normalizeAddress(pendingDisputeAdmin),
        disputeAdminEffectiveAt: toNumber(disputeAdminEffectiveAt),
        whitelistedTokens: mapWhitelistedTokens(tokenAddresses, tokenList),
        fetchedAt: Date.now(),
    };
}

export function isWalletContractOwner(walletAddress, ownerAddress) {
    const wallet = String(walletAddress || '').trim().toLowerCase();
    const owner = String(ownerAddress || '').trim().toLowerCase();
    return Boolean(wallet && owner && wallet === owner);
}
